import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { motion } from "framer-motion";
import {
  PlusCircle,
  Phone,
  MapPin,
  User,
  Coins,
  Trash2,
  ClipboardPaste,
  CalendarDays,
} from "lucide-react";
import logo from "../assets/logo.png";
import jsPDF from "jspdf";

/* ================= CONFIG ================= */
const DAILY_RATE = 500; // 👈 optional default rate per day (can change)

/* ================= PAGE ================= */
export default function AdminDashboard() {
  const [dark, setDark] = useState(true);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    location: "",
    routeStops: [""],
    eventStart: "",
    eventEnd: "",
    amountCharged: "",
    amountPaid: "",
  });

  /* ================= THEME SYNC ================= */
  useEffect(() => {
    const syncTheme = () => {
      setDark(localStorage.getItem("theme") !== "light");
    };
    syncTheme();
    window.addEventListener("theme-change", syncTheme);
    return () => window.removeEventListener("theme-change", syncTheme);
  }, []);

  /* ================= ROUTE ================= */
  const updateStop = (i, v) => {
    const s = [...form.routeStops];
    s[i] = v;
    setForm({ ...form, routeStops: s });
  };

  const addStop = () =>
    setForm((f) => ({ ...f, routeStops: [...f.routeStops, ""] }));

  const removeStop = (i) =>
    setForm((f) => ({
      ...f,
      routeStops: f.routeStops.filter((_, x) => x !== i),
    }));

  const routeText = form.routeStops.filter(Boolean).join(" → ");
  const cleanRoute = routeText.replace(/→/g, "->");

  /* ================= EVENT DAYS ================= */
  const eventDays = useMemo(() => {
    if (!form.eventStart || !form.eventEnd) return 0;
    const start = new Date(form.eventStart);
    const end = new Date(form.eventEnd);
    const diff = Math.ceil((end - start) / 86400000) + 1;
    return diff > 0 ? diff : 0;
  }, [form.eventStart, form.eventEnd]);

  /* ================= AUTO AMOUNT (OPTIONAL) ================= */
 useEffect(() => {
  setForm((f) => {
    if (eventDays > 0 && !f.amountCharged) {
      return {
        ...f,
        amountCharged: String(eventDays * DAILY_RATE),
      };
    }
    return f;
  });
}, [eventDays]);


  /* ================= BALANCE ================= */
  const amountCharged = Number(form.amountCharged || 0);
  const amountPaid = Number(form.amountPaid || 0);
  const balance = amountCharged - amountPaid;
  const overPaid = balance < 0;

  const receiptNo = `DH-${Date.now()}`;

  /* ================= THEME MAP ================= */
  const theme = {
    page: dark ? "bg-[#0b0b0b] text-white" : "bg-gray-100 text-gray-900",
    card: dark ? "bg-white/5 border-white/20" : "bg-white border-gray-300",
    inputBg: dark ? "bg-black/40 text-white" : "bg-white text-gray-900",
    inputBorder: dark ? "border-white/20" : "border-gray-300",
    label: dark ? "text-gray-300" : "text-gray-700",
    muted: dark ? "text-gray-400" : "text-gray-600",
    btn: dark
      ? "bg-gradient-to-r from-sky-600 to-sky-700"
      : "bg-sky-600 hover:bg-sky-700",
  };

  /* ================= PASTE ================= */
  const pasteCustomer = async () => {
    const text = await navigator.clipboard.readText();
    const get = (k) => {
      const m = text.match(new RegExp(`${k}:\\s*(.+)`));
      return m ? m[1].trim() : "";
    };

    const routeRaw = get("Route");
    const eventRaw = get("Event");

    setForm((f) => ({
      ...f,
      name: get("Name") || f.name,
      phone: get("Phone") || f.phone,
      location: get("Location") || f.location,
      routeStops: routeRaw
        ? routeRaw.split(/→|->/).map((s) => s.trim())
        : f.routeStops,
      eventStart: eventRaw ? eventRaw.split(/→|->/)[0]?.trim() : f.eventStart,
      eventEnd: eventRaw ? eventRaw.split(/→|->/)[1]?.trim() : f.eventEnd,
      amountCharged: get("Amount") || f.amountCharged,
      amountPaid: get("Paid") || f.amountPaid,
    }));
  };
const safeText = (text = "") =>
  String(text)
    .replace(/→/g, "->")
    .replace(/[^\x20-\x7E]/g, "");


 const downloadPDF = () => {
  const doc = new jsPDF("p", "mm", "a4");
  let y = 20;

  /* ================= HEADER ================= */
  doc.addImage(logo, "PNG", 90, y, 30, 30);
  y += 40;

  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text("Deonyak Hearse Service", 105, y, { align: "center" });

  y += 6;
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.text("0245226992 / 0244378871", 105, y, { align: "center" });

  y += 12;

  /* ================= META ================= */
  doc.text(`Receipt No: ${receiptNo}`, 20, y);
  doc.text(`Date: ${new Date().toLocaleString()}`, 130, y);

  y += 12;

  /* ================= CUSTOMER DETAILS ================= */
  doc.setFont(undefined, "bold");
  doc.text("Hearse Booking Details", 20, y);
  y += 8;

  doc.setFont(undefined, "normal");
  doc.text(`Name: ${form.name}`, 20, y); y += 6;
  doc.text(`Phone: ${form.phone}`, 20, y); y += 6;
  doc.text(`Location: ${form.location}`, 20, y); y += 6;
  doc.text(`Route: ${safeText(cleanRoute)}`, 20, y); y += 6;
  doc.text(
    `Event: ${safeText(`${form.eventStart} -> ${form.eventEnd}`)}`,
    20,
    y
  );

  y += 12;

  /* ================= PAYMENT ================= */
  doc.text(`Amount Charged: GHS ${amountCharged}`, 20, y); y += 6;
  doc.text(`Amount Paid: GHS ${amountPaid}`, 20, y); y += 6;
  doc.text(`Balance: GHS ${balance}`, 20, y);

  y += 30;

  /* ================= SIGNATURE ================= */
  doc.text("Authorized Signature:", 20, y);
  y += 8;
  doc.line(20, y, 90, y);

  y += 20;

  /* ================= FOOTER ================= */
  doc.setFontSize(9);
  doc.text(
    "Thank you for choosing Deonyak Hearse Service",
    105,
    y,
    { align: "center" }
  );

  doc.save(`Deonyak_Receipt_${receiptNo}.pdf`);
};

  /* ================= SAVE ================= */
  const saveService = async () => {
    if (!form.name || !form.phone || !routeText || !form.eventStart) {
      alert("Missing required fields");
      return;
    }
    if (overPaid) {
      alert("Amount paid exceeds amount charged");
      return;
    }

    await addDoc(collection(db, "services"), {
      ...form,
      route: routeText,
      eventDays,
      balance,
      receiptNo,
      createdAt: serverTimestamp(),
      admin: auth.currentUser.email,
    });

    downloadPDF();

    setForm({
      name: "",
      phone: "",
      location: "",
      routeStops: [""],
      eventStart: "",
      eventEnd: "",
      amountCharged: "",
      amountPaid: "",
    });
  };

  /* ================= UI ================= */
  return (
    <div className={`min-h-screen px-6 py-16 ${theme.page}`}>
      <motion.div className={`max-w-3xl mx-auto rounded-3xl border p-8 ${theme.card}`}>
        <div className="flex justify-between mb-8">
          <h2 className="text-2xl font-bold flex gap-2">
            <PlusCircle /> New Hearse Service
          </h2>
          <button onClick={pasteCustomer} className="flex gap-2 px-4 py-2 border rounded-xl">
            <ClipboardPaste size={16} /> Paste
          </button>
        </div>

        {/* CUSTOMER */}
        <Grid>
          <Field icon={User} label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} theme={theme} />
          <Field icon={Phone} label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} theme={theme} />
          <Field icon={MapPin} label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} theme={theme} />
        </Grid>

        {/* EVENT */}
        <Grid>
          <Field icon={CalendarDays} type="date" label="Event Start" value={form.eventStart} onChange={(v) => setForm({ ...form, eventStart: v })} theme={theme} />
          <Field icon={CalendarDays} type="date" label="Event End" value={form.eventEnd} onChange={(v) => setForm({ ...form, eventEnd: v })} theme={theme} />
        </Grid>

        {eventDays > 0 && (
          <p className={`mt-2 text-sm ${theme.muted}`}>
            Duration: <strong>{eventDays} day(s)</strong>
          </p>
        )}

        {/* ROUTE */}
        <div className="mt-6 space-y-3">
          {form.routeStops.map((s, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={s}
                onChange={(e) => updateStop(i, e.target.value)}
                className={`flex-1 px-4 py-3 rounded-xl border ${theme.inputBg} ${theme.inputBorder}`}
                placeholder={`Stop ${i + 1}`}
              />
              {form.routeStops.length > 1 && (
                <button onClick={() => removeStop(i)} className="text-red-400">
                  <Trash2 />
                </button>
              )}
            </div>
          ))}
          <button onClick={addStop} className="px-4 py-2 bg-sky-600 text-white rounded-xl">
            Add Route
          </button>
        </div>

        {/* PAYMENT */}
        <Grid>
          <Field icon={Coins} label="Amount Charged" type="number" value={form.amountCharged} onChange={(v) => setForm({ ...form, amountCharged: v })} theme={theme} />
          <Field icon={Coins} label="Amount Paid" type="number" value={form.amountPaid} onChange={(v) => setForm({ ...form, amountPaid: v })} theme={theme} />
        </Grid>

        <p className={`mt-4 font-semibold ${overPaid ? "text-red-500" : "text-green-500"}`}>
          Balance: GHS {balance}
        </p>

        <button onClick={saveService} className={`mt-8 w-full py-4 rounded-xl ${theme.btn}`}>
          Save & Download Receipt
        </button>
      </motion.div>
    </div>
  );
}

/* ================= HELPERS ================= */
function Grid({ children }) {
  return <div className="grid md:grid-cols-2 gap-6 mt-6">{children}</div>;
}

function Field({ icon: Icon, label, value, onChange, type = "text", theme }) {
  return (
    <div>
      <label className={`text-sm mb-1 block ${theme.label}`}>{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-3.5 text-gray-400" size={18} />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full pl-10 pr-3 py-3 rounded-xl border ${theme.inputBg} ${theme.inputBorder}`}
        />
      </div>
    </div>
  );
}
